import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  testGeneratorMetadata,
  generateTestAsync,
  getTestGenerationStatus,
  refreshQuestion,
  finalizeTest,
  publishTest,
  teacherStudents,
} from "@/lib/api";
import {
  Loader2,
  Check,
  X,
  RefreshCw,
  Edit2,
  FileText,
  Download,
  Wand2,
  PenLine,
  Users,
  CheckSquare,
  Square,
  BookOpen,
  Send,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import TestPDF from "./TestPDF";
import ManualTestBuilder from "./ManualTestBuilder";

/**
 * TestGeneratorModal — Full practice test generator flow
 * 
 * Phases:
 * 0. mode_select: Choose Auto-Generated or Manual
 * 1. form: Teacher configures test parameters
 * 2. loading: Generating questions via Claude API
 * 3. review: Teacher reviews auto-generated questions
 * 4. manual_builder: Teacher builds questions manually
 * 5. preview/publish: View and publish
 */
export default function TestGeneratorModal({
  open,
  onClose,
  teacherId,
  grade,
  // Pre-fill props (from student journey entry point)
  preSelectedSubject = null,
  preSelectedSubTopics = [],
  learnerName = null,
  learnerId = null,
}) {
  const [phase, setPhase] = useState("mode_select"); // mode_select | form | loading | review | manual_builder | manual_preview | preview
  const [mode, setMode] = useState(null); // "auto" | "manual"
  const [loading, setLoading] = useState(false);
  const [topicMode, setTopicMode] = useState("single"); // "single" | "multi"

  // Timer state
  const [timerMode, setTimerMode] = useState("auto"); // "auto" | "custom" | "none"
  const [customTimerMins, setCustomTimerMins] = useState(20);

  // Async generation job state
  const [jobId, setJobId] = useState(null);
  const [pollProgress, setPollProgress] = useState({ completed: 0, total: 0 });
  const pollRef = useRef(null);

  // Local grade state — allows grade to be set inside the modal when not pre-selected
  const [localGrade, setLocalGrade] = useState(grade || null);

  // Form state
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [subTopics, setSubTopics] = useState([]);
  const [formData, setFormData] = useState({
    subject: preSelectedSubject || "",
    chapter: "",
    selectedSubTopics: preSelectedSubTopics || [],
    selectedTopics: [], // multi-topic mode
    difficulty: "Medium",
    numQuestions: 10,
    addMarks: false,
    fullChapterTest: false,
  });

  // Manual publish — student selection state (for class tests)
  const [showManualStudentSelect, setShowManualStudentSelect] = useState(false);
  const [manualStudentList, setManualStudentList] = useState([]);
  const [manualSelectedIds, setManualSelectedIds] = useState([]);
  const [manualMarksOption, setManualMarksOption] = useState(false);
  const [manualStudentsLoading, setManualStudentsLoading] = useState(false);

  // Generated test state
  const [testData, setTestData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionStates, setQuestionStates] = useState({}); // { [questionNumber]: "approved" | "rejected" | "editing" }
  const [editingQuestion, setEditingQuestion] = useState(null); // { questionNumber, text, options, correctAnswer }

  // Manual builder state
  const [manualQuestions, setManualQuestions] = useState([]);
  const [manualPublishLoading, setManualPublishLoading] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);

  // Sync grade prop → localGrade and fetch metadata when modal opens
  useEffect(() => {
    if (open) {
      // Sync localGrade from grade prop so navigation to /cockpit/:grade works correctly
      if (grade) setLocalGrade(grade);
      setPhase("mode_select");
      setMode(null);
      fetchMetadata();
    }
  }, [open]);

  // Re-fetch subjects when grade changes
  useEffect(() => {
    if (open && localGrade) {
      // Reset downstream selections when grade changes
      setFormData(prev => ({ ...prev, subject: preSelectedSubject || "", chapter: "", selectedSubTopics: [] }));
      setChapters([]);
      setSubTopics([]);
      fetchMetadata();
    }
  }, [localGrade]);

  // ── Polling effect for async test generation ──────────────────────────────
  useEffect(() => {
    if (!jobId) return;

    pollRef.current = setInterval(async () => {
      try {
        const status = await getTestGenerationStatus(jobId);
        setPollProgress({ completed: status.completed, total: status.total });

        if (status.status === "done") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setJobId(null);

          const result = status.result;
          setTestData(result);

          const questionsWithAnswers = (result.questions || []).map(q => ({
            ...q,
            correct_answer: q._internal_correct_answer || q.correct_answer,
          }));
          setQuestions(questionsWithAnswers);

          const initialStates = {};
          questionsWithAnswers.forEach(q => { initialStates[q.question_number] = "pending"; });
          setQuestionStates(initialStates);

          setPhase("review");
          toast.success("Test generated successfully!");
        } else if (status.status === "error") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setJobId(null);
          toast.error("Failed to generate test: " + (status.error || "Unknown error"));
          setPhase("form");
        }
      } catch (err) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setJobId(null);
        toast.error("Lost connection to generation job. Please try again.");
        setPhase("form");
      }
    }, 2500);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [jobId]);

  // Fetch metadata for dropdowns
  const fetchMetadata = async () => {
    try {
      const data = await testGeneratorMetadata(null, null, localGrade);
      setSubjects(data.subjects || []);
      // Reset chapter/subtopic selections when subjects change
      setChapters([]);
      setSubTopics([]);
      
      // If pre-selected subject, fetch chapters immediately
      if (preSelectedSubject) {
        const chapterData = await testGeneratorMetadata(preSelectedSubject, null, localGrade);
        setChapters(chapterData.chapters || []);
      }
    } catch (err) {
      toast.error("Failed to load form options");
    }
  };

  // Handle subject change
  const handleSubjectChange = async (value) => {
    setFormData({ ...formData, subject: value, chapter: "", selectedSubTopics: [], selectedTopics: [] });
    setChapters([]);
    setSubTopics([]);
    
    try {
      const data = await testGeneratorMetadata(value, null, localGrade);
      setChapters(data.chapters || []);
    } catch (err) {
      toast.error("Failed to load chapters");
    }
  };

  // Toggle topic (multi-topic mode)
  const toggleTopic = (topic) => {
    setFormData(prev => {
      const already = prev.selectedTopics.includes(topic);
      return {
        ...prev,
        selectedTopics: already
          ? prev.selectedTopics.filter(t => t !== topic)
          : [...prev.selectedTopics, topic],
      };
    });
  };

  // Handle chapter change
  const handleChapterChange = async (value) => {
    setFormData({ ...formData, chapter: value, selectedSubTopics: [] });
    setSubTopics([]);
    
    try {
      const data = await testGeneratorMetadata(formData.subject, value, localGrade);
      setSubTopics(data.sub_topics || []);
    } catch (err) {
      toast.error("Failed to load sub-topics");
    }
  };

  // Toggle sub-topic selection
  const toggleSubTopic = (st) => {
    const selected = formData.selectedSubTopics;
    const newSelected = selected.includes(st)
      ? selected.filter((s) => s !== st)
      : [...selected, st];
    
    console.log(`Toggle sub-topic: ${st}, was ${selected.includes(st) ? 'checked' : 'unchecked'}, now ${!selected.includes(st) ? 'checked' : 'unchecked'}`);
    console.log("New selected sub-topics:", newSelected);
    
    setFormData({
      ...formData,
      selectedSubTopics: newSelected,
    });
  };

  // Go to manual builder phase (after form filled)
  const handleProceedToManualBuilder = () => {
    if (!localGrade) { toast.error("Please select a grade"); return; }
    if (!formData.subject) { toast.error("Please select a subject"); return; }
    if (!formData.chapter) { toast.error("Please select a chapter"); return; }
    // Initialize with one empty question
    setManualQuestions([{
      question_number: 1,
      question_text: "",
      question_image: null,
      answer_type: "MCQ",
      options: ["", "", "", ""],
      correct_answer: "",
      marks: 1,
    }]);
    setPhase("manual_builder");
  };

  // Validate manual questions and transition to preview phase
  const handleManualPreview = () => {
    const errors = [];
    manualQuestions.forEach((q, idx) => {
      if (!q.question_text.trim()) errors.push(`Q${idx + 1}: question text is empty`);
      if (q.answer_type === "MCQ") {
        const filled = (q.options || []).filter((o) => o.trim());
        if (filled.length < 2) errors.push(`Q${idx + 1}: add at least 2 options`);
        if (!q.correct_answer) errors.push(`Q${idx + 1}: mark the correct option`);
      }
    });
    if (errors.length > 0) {
      errors.forEach((e) => toast.error(e));
      return;
    }
    setPhase("manual_preview");
  };

  // Finalize + publish manual test
  const handleManualPublish = async (addMarks, publishLearnerIds = null) => {
    setManualPublishLoading(true);
    setShowPublishDialog(false);
    setShowManualStudentSelect(false);
    try {
      const testId = `manual-${Date.now()}`;
      const totalMarks = addMarks ? manualQuestions.reduce((s, q) => s + (q.marks || 1), 0) : null;

      await finalizeTest({
        test_id: testId,
        teacher_id: teacherId,
        grade: localGrade,
        subject: formData.subject,
        chapter: formData.chapter,
        sub_topics: [formData.chapter],
        topics: [],
        questions: manualQuestions,
        add_marks: addMarks,
        marks_per_question: null,
        learner_id: learnerId,
        learner_name: learnerName,
        mode: "manual",
        timer_minutes: timerMode === "none" ? null : customTimerMins,
      });

      const pubPayload = { test_id: testId, teacher_id: teacherId, grade: localGrade };
      if (publishLearnerIds) {
        pubPayload.learner_ids = publishLearnerIds;
      } else if (learnerId) {
        pubPayload.learner_ids = [learnerId];
      } else {
        pubPayload.class_id = `${teacherId}-grade-${localGrade}`;
      }

      const result = await publishTest(pubPayload);

      setTestData({
        test_id: testId,
        subject: formData.subject,
        chapter: formData.chapter,
        sub_topics: [formData.chapter],
        topics: [],
        grade: localGrade,
        questions: manualQuestions,
        add_marks: addMarks,
        total_marks: totalMarks,
        learner_id: learnerId,
        learner_name: learnerName,
        mode: "manual",
        status: "published",
      });
      setPhase("preview");
      toast.success(`Test published to ${result.published_count} student${result.published_count === 1 ? "" : "s"}!`);
    } catch (err) {
      toast.error("Failed to publish: " + (err.response?.data?.detail || err.message));
    } finally {
      setManualPublishLoading(false);
    }
  };

  // Open manual publish flow — student select for class tests, marks dialog for individual
  const handleManualPublishClick = async () => {
    if (learnerId) {
      // Individual student — just ask marks
      setShowPublishDialog(true);
    } else {
      // Class test — load students then open student select
      if (manualStudentList.length === 0) {
        setManualStudentsLoading(true);
        try {
          const data = await teacherStudents(teacherId, localGrade);
          const list = Array.isArray(data) ? data : data.students || [];
          setManualStudentList(list);
          setManualSelectedIds(list.map(s => s.learner_id));
        } catch (e) {
          toast.error("Could not load students: " + e.message);
          setManualStudentsLoading(false);
          return;
        } finally {
          setManualStudentsLoading(false);
        }
      }
      setShowManualStudentSelect(true);
    }
  };

  // Generate test — async background job + polling
  const handleGenerate = async () => {
    if (!localGrade) {
      toast.error("Please select a grade before generating a test");
      return;
    }
    if (!formData.subject) {
      toast.error("Please select a subject");
      return;
    }

    // ── Multi-topic validation ────────────────────────────────────────────────
    if (topicMode === "multi") {
      if (formData.selectedTopics.length < 1) {
        toast.error("Please select at least one chapter");
        return;
      }
    } else {
      // ── Single-topic validation ───────────────────────────────────────────
      if (!formData.chapter) {
        toast.error("Please select a chapter");
        return;
      }
      if (formData.selectedSubTopics.length === 0 && !formData.fullChapterTest) {
        toast.error("Please select at least one sub-topic or enable 'Full Chapter Test'");
        return;
      }
    }

    setLoading(true);
    setPhase("loading");
    const estimatedTotal = topicMode === "multi"
      ? formData.selectedTopics.length
      : (formData.selectedSubTopics.length || 1);
    setPollProgress({ completed: 0, total: estimatedTotal });

    try {
      const payload = {
        teacher_id: teacherId,
        grade: localGrade,
        subject: formData.subject,
        difficulty: formData.difficulty,
        num_questions: formData.numQuestions,
        add_marks: formData.addMarks,
        learner_id: learnerId,
        learner_name: learnerName,
        // Mode-specific fields
        test_mode: topicMode === "multi" ? "multi_topic" : "single_topic",
        topics: topicMode === "multi" ? formData.selectedTopics : [],
        chapter: topicMode === "single" ? formData.chapter : "",
        sub_topics: topicMode === "single" ? formData.selectedSubTopics : [],
        full_chapter_test: topicMode === "single" ? formData.fullChapterTest : false,
      };

      const { job_id, total } = await generateTestAsync(payload);
      setPollProgress({ completed: 0, total: total || estimatedTotal });
      setJobId(job_id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errMsg = Array.isArray(detail)
        ? detail.map(e => e.msg || e.message || String(e)).join("; ")
        : typeof detail === "string"
        ? detail
        : err.message || "Unknown error";
      toast.error("Failed to start test generation: " + errMsg);
      setPhase("form");
    } finally {
      setLoading(false);
    }
  };

  // Action handlers
  const handleApprove = (questionNumber) => {
    setQuestionStates({ ...questionStates, [questionNumber]: "approved" });
    toast.success(`Question ${questionNumber} approved`);
  };

  const handleReject = (questionNumber) => {
    setQuestionStates({ ...questionStates, [questionNumber]: "rejected" });
    toast("Question rejected. Use Refresh to replace it.");
  };

  const handleEdit = (question) => {
    setEditingQuestion({
      questionNumber: question.question_number,
      text: question.question_text,
      options: [...question.options],
      correctAnswer: question.correct_answer,
    });
  };

  const saveEdit = () => {
    if (!editingQuestion) return;
    
    const updatedQuestions = questions.map((q) =>
      q.question_number === editingQuestion.questionNumber
        ? {
            ...q,
            question_text: editingQuestion.text,
            options: editingQuestion.options,
            correct_answer: editingQuestion.correctAnswer,
          }
        : q
    );
    
    setQuestions(updatedQuestions);
    setQuestionStates({ ...questionStates, [editingQuestion.questionNumber]: "approved" });
    setEditingQuestion(null);
    toast.success("Question updated");
  };

  const handleRefresh = async (question) => {
    toast("Fetching replacement question...");
    
    try {
      // Get all current question texts to exclude
      const excludeTexts = questions.map((q) => q.question_text);
      
      const payload = {
        grade: localGrade,  // Pass selected grade from teacher context
        subject: formData.subject,
        chapter: formData.chapter,
        sub_topic: question.sub_topic,
        difficulty: question.difficulty,
        exclude_texts: excludeTexts,
      };
      
      const newQuestion = await refreshQuestion(payload);
      
      // Replace the question
      const updatedQuestions = questions.map((q) =>
        q.question_number === question.question_number
          ? {
              ...q,
              question_text: newQuestion.question_text,
              options: newQuestion.options,
              correct_answer: newQuestion.correct_answer,
            }
          : q
      );
      
      setQuestions(updatedQuestions);
      setQuestionStates({ ...questionStates, [question.question_number]: "pending" });
      toast.success("Question refreshed");
    } catch (err) {
      toast.error("Failed to refresh question");
    }
  };

  const handleRegenerateAll = () => {
    setPhase("form");
    setQuestions([]);
    setQuestionStates({});
    setTestData(null);
    toast("Cleared. Configure and generate again.");
  };

  const handleApproveAll = () => {
    const newStates = {};
    questions.forEach((q) => {
      newStates[q.question_number] = "approved";
    });
    setQuestionStates(newStates);
    toast.success(`All ${questions.length} questions approved!`);
  };

  const handleFinalize = async () => {
    // Check if all questions are approved
    const allApproved = questions.every(
      (q) => questionStates[q.question_number] === "approved"
    );
    
    if (!allApproved) {
      toast.error("Please approve all questions before finalizing");
      return;
    }

    setLoading(true);
    try {
      await finalizeTest({
        test_id: testData.test_id,
        teacher_id: teacherId,
        grade: localGrade,
        subject: testData.subject,
        chapter: testData.chapter,
        sub_topics: testData.sub_topics,
        topics: testData.topics || [],
        questions: questions,
        add_marks: testData.add_marks,
        marks_per_question: null,
        learner_id: learnerId,
        timer_minutes: timerMode === "none" ? null : (timerMode === "auto" ? 2 * formData.numQuestions : customTimerMins),
      });
      
      setPhase("preview");
      toast.success("Test finalized!");
    } catch (err) {
      toast.error("Failed to finalize test: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setJobId(null);
    setPollProgress({ completed: 0, total: 0 });
    setPhase("mode_select");
    setMode(null);
    setTopicMode("single");
    setLocalGrade(grade || null);
    setTimerMode("auto");
    setCustomTimerMins(20);
    setFormData({
      subject: preSelectedSubject || "",
      chapter: "",
      selectedSubTopics: preSelectedSubTopics || [],
      selectedTopics: [],
      difficulty: "Medium",
      numQuestions: 10,
      addMarks: false,
      marksPerQuestion: 1,
      fullChapterTest: false,
    });
    setQuestions([]);
    setQuestionStates({});
    setTestData(null);
    setEditingQuestion(null);
    setManualQuestions([]);
    setShowPublishDialog(false);
    setShowManualStudentSelect(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[calc(100%-1rem)] sm:w-full sm:max-w-5xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="font-teacher text-2xl">
            {learnerName ? `Generate Test for ${learnerName}` : "Generate Practice Test"}
          </DialogTitle>
        </DialogHeader>

        {/* Phase 0: Mode Select */}
        {phase === "mode_select" && (
          <div className="py-6 space-y-4">
            <p className="text-slate-600 text-sm">How would you like to create this test?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => { setMode("auto"); setPhase("form"); }}
                data-testid="mode-auto-btn"
                className="group rounded-2xl p-6 bg-sky-50 border-2 border-sky-200 hover:border-sky-500 hover:shadow-md text-left transition"
              >
                <div className="w-11 h-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center mb-4">
                  <Wand2 className="w-5 h-5" />
                </div>
                <h3 className="font-teacher text-lg font-bold text-sky-900">Auto-Generated</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Mitraa picks questions from the dataset for you. Fast and curriculum-aligned.
                </p>
              </button>

              <button
                onClick={() => { setMode("manual"); setPhase("form"); }}
                data-testid="mode-manual-btn"
                className="group rounded-2xl p-6 bg-violet-50 border-2 border-violet-200 hover:border-violet-500 hover:shadow-md text-left transition"
              >
                <div className="w-11 h-11 rounded-xl bg-violet-100 text-violet-700 flex items-center justify-center mb-4">
                  <PenLine className="w-5 h-5" />
                </div>
                <h3 className="font-teacher text-lg font-bold text-violet-900">Manual</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Write your own questions with MCQ or free-text (TITA) answer types.
                </p>
              </button>
            </div>
            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
            </div>
          </div>
        )}

        {/* Phase 1: Form */}
        {phase === "form" && (
          <div className="space-y-6 py-4">
            {/* Grade — shown only when not pre-selected from context */}
            {!grade && (
              <div>
                <Label className="text-sm font-semibold mb-2 block">Grade</Label>
                <Select
                  value={localGrade ? String(localGrade) : ""}
                  onValueChange={(v) => setLocalGrade(parseInt(v, 10))}
                >
                  <SelectTrigger className="w-full" data-testid="grade-select">
                    <SelectValue placeholder="Select grade" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((g) => (
                      <SelectItem key={g} value={String(g)}>Grade {g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Subject */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">Subject</Label>
              <Select
                value={formData.subject}
                onValueChange={handleSubjectChange}
                disabled={!!preSelectedSubject}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select subject" />
                </SelectTrigger>
                <SelectContent>
                  {subjects.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Topic mode — only shown in auto mode when subject is selected */}
            {mode !== "manual" && formData.subject && (
              <div>
                <Label className="text-sm font-semibold mb-2 block">Test Scope</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setTopicMode("single"); setFormData(prev => ({ ...prev, selectedTopics: [] })); }}
                    data-testid="topic-mode-single"
                    className={`flex-1 flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                      topicMode === "single"
                        ? "border-sky-500 bg-sky-50 text-sky-700"
                        : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    <FileText className="w-4 h-4 flex-shrink-0" />
                    Single Chapter
                  </button>
                  <button
                    type="button"
                    onClick={() => { setTopicMode("multi"); setFormData(prev => ({ ...prev, chapter: "", selectedSubTopics: [] })); }}
                    data-testid="topic-mode-multi"
                    className={`flex-1 flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                      topicMode === "multi"
                        ? "border-violet-500 bg-violet-50 text-violet-700"
                        : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    <BookOpen className="w-4 h-4 flex-shrink-0" />
                    Multiple Chapters
                  </button>
                </div>
              </div>
            )}

            {/* Single-topic: Chapter select */}
            {(mode === "manual" || topicMode === "single") && formData.subject && (
              <div>
                <Label className="text-sm font-semibold mb-2 block">Chapter</Label>
                <Select value={formData.chapter} onValueChange={handleChapterChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select chapter" />
                  </SelectTrigger>
                  <SelectContent>
                    {chapters.map((ch) => (
                      <SelectItem key={ch} value={ch}>{ch}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Multi-topic: Chapter checkboxes */}
            {topicMode === "multi" && mode !== "manual" && formData.subject && chapters.length > 0 && (
              <div>
                <Label className="text-sm font-semibold mb-2 block">
                  Chapters{" "}
                  <span className="text-slate-500 font-normal">(select one or more — questions distributed proportionally)</span>
                </Label>
                <div className="border border-slate-200 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                  {chapters.map((ch) => (
                    <div key={ch} className="flex items-center gap-2">
                      <Checkbox
                        id={`topic-${ch}`}
                        checked={formData.selectedTopics.includes(ch)}
                        onCheckedChange={() => toggleTopic(ch)}
                      />
                      <Label htmlFor={`topic-${ch}`} className="text-sm cursor-pointer flex-1">{ch}</Label>
                    </div>
                  ))}
                </div>
                {formData.selectedTopics.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.selectedTopics.length} chapter{formData.selectedTopics.length !== 1 ? "s" : ""} selected
                    — {formData.numQuestions} questions distributed proportionally across chapters
                  </p>
                )}
              </div>
            )}

            {/* Sub-topics (multi-select) — single-topic mode only */}
            {topicMode === "single" && formData.chapter && subTopics.length > 0 && (
              <div>
                <Label className="text-sm font-semibold mb-2 block">
                  Sub-topics{" "}
                  <span className="text-slate-500 font-normal">(select one or more)</span>
                </Label>
                <div className="border border-slate-200 rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                  {subTopics.map((st) => (
                    <div key={st} className="flex items-center gap-2">
                      <Checkbox
                        id={`st-${st}`}
                        checked={formData.selectedSubTopics.includes(st)}
                        onCheckedChange={() => toggleSubTopic(st)}
                        disabled={formData.fullChapterTest}
                      />
                      <Label
                        htmlFor={`st-${st}`}
                        className={`text-sm cursor-pointer flex-1 ${formData.fullChapterTest ? 'text-slate-400' : ''}`}
                      >
                        {st}
                      </Label>
                    </div>
                  ))}
                  {/* Full Chapter Test option */}
                  <div className="border-t pt-2 mt-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="full-chapter-test"
                        checked={formData.fullChapterTest}
                        onCheckedChange={(checked) => {
                          setFormData({
                            ...formData,
                            fullChapterTest: checked,
                            selectedSubTopics: checked ? [] : formData.selectedSubTopics,
                          });
                        }}
                      />
                      <Label
                        htmlFor="full-chapter-test"
                        className="text-sm cursor-pointer flex-1 font-semibold text-sky-700"
                      >
                        Full Chapter Test (all sub-topics)
                      </Label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Difficulty — auto mode only */}
            {mode !== "manual" && (
            <div>
              <Label className="text-sm font-semibold mb-2 block">Difficulty</Label>
              <Select
                value={formData.difficulty}
                onValueChange={(v) => setFormData({ ...formData, difficulty: v })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Easy">Easy</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Hard">Hard</SelectItem>
                  <SelectItem value="Mixed">Mixed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}

            {/* Number of questions — auto mode only */}
            {mode !== "manual" && (
            <div>
              <Label className="text-sm font-semibold mb-2 block">Number of Questions</Label>
              <Select
                value={String(formData.numQuestions)}
                onValueChange={(v) =>
                  setFormData({ ...formData, numQuestions: parseInt(v, 10) })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="15">15</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}

            {/* Add marks toggle — auto mode only */}
            {mode !== "manual" && (
            <div className="flex items-center gap-3">
              <Checkbox
                id="add-marks"
                checked={formData.addMarks}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, addMarks: checked })
                }
              />
              <Label htmlFor="add-marks" className="text-sm cursor-pointer">
                Add marks per question <span className="text-slate-500 font-normal">(auto-suggested based on difficulty, editable per question)</span>
              </Label>
            </div>
            )}

            {/* Timer */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">
                Time Limit
                <span className="text-slate-500 font-normal ml-1">(students see a countdown clock)</span>
              </Label>
              <div className="flex gap-2 mb-2">
                {mode !== "manual" && (
                  <button
                    type="button"
                    onClick={() => setTimerMode("auto")}
                    data-testid="timer-mode-auto"
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border-2 text-sm font-semibold transition-all ${
                      timerMode === "auto"
                        ? "border-sky-500 bg-sky-50 text-sky-700"
                        : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                    Auto ({2 * formData.numQuestions} min)
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setTimerMode("custom")}
                  data-testid="timer-mode-custom"
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border-2 text-sm font-semibold transition-all ${
                    timerMode === "custom"
                      ? "border-amber-500 bg-amber-50 text-amber-700"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  }`}
                >
                  <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                  Custom
                </button>
                <button
                  type="button"
                  onClick={() => setTimerMode("none")}
                  data-testid="timer-mode-none"
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border-2 text-sm font-semibold transition-all ${
                    timerMode === "none"
                      ? "border-slate-500 bg-slate-50 text-slate-700"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  }`}
                >
                  No Timer
                </button>
              </div>
              {timerMode === "custom" && (
                <Select
                  value={String(customTimerMins)}
                  onValueChange={(v) => setCustomTimerMins(parseInt(v, 10))}
                >
                  <SelectTrigger className="w-full" data-testid="custom-timer-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 10, 15, 20, 25, 30, 45, 60].map((m) => (
                      <SelectItem key={m} value={String(m)}>
                        {m} minutes
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Generate button */}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setPhase("mode_select")}>
                Back
              </Button>
              {mode === "manual" ? (
                <Button onClick={handleProceedToManualBuilder} className="bg-violet-600 hover:bg-violet-700">
                  <PenLine className="w-4 h-4 mr-2" /> Build Questions
                </Button>
              ) : (
                <Button onClick={handleGenerate} className="bg-sky-600 hover:bg-sky-700">
                  Generate Test
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Phase 2: Loading */}
        {phase === "loading" && (
          <div className="py-20 text-center space-y-4">
            <Loader2 className="w-12 h-12 animate-spin mx-auto text-sky-600" />
            <p className="text-slate-700 font-semibold text-lg">Preparing your question set...</p>
            {pollProgress.total > 0 && (
              <>
                <p className="text-sm text-slate-500">
                  {jobId
                    ? topicMode === "multi"
                      ? `Generating across ${formData.selectedTopics.length} chapter${formData.selectedTopics.length !== 1 ? "s" : ""}… (${pollProgress.completed} of ${pollProgress.total} ready)`
                      : `${pollProgress.completed} of ${pollProgress.total} sub-topic${pollProgress.total !== 1 ? "s" : ""} ready`
                    : "Starting..."}
                </p>
                {/* Progress bar */}
                <div className="mx-auto w-64 bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-2 bg-sky-500 rounded-full transition-all duration-500"
                    style={{
                      width: pollProgress.total > 0
                        ? `${Math.round((pollProgress.completed / pollProgress.total) * 100)}%`
                        : "0%"
                    }}
                  />
                </div>
              </>
            )}
            <p className="text-xs text-slate-400">
              {jobId ? "Questions are being prepared in parallel — this is faster than before!" : "Starting generation job..."}
            </p>
          </div>
        )}

        {/* Phase 3: Review */}
        {phase === "review" && (
          <div className="space-y-4 py-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <div className="text-sm text-slate-600">
                Review and approve each question below
                {testData?.topics && testData.topics.length > 1 && (
                  <span className="ml-2 text-violet-600 font-semibold">
                    · {testData.topics.length} chapters
                  </span>
                )}
                {testData?.add_marks && (
                  <span className="ml-2 font-semibold text-sky-700">
                    Total: {questions.reduce((sum, q) => sum + (q.marks || 0), 0)} marks
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleApproveAll}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                >
                  <Check className="w-3.5 h-3.5 mr-1" /> Approve All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRegenerateAll}
                  className="text-xs"
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerate All
                </Button>
              </div>
            </div>

            {/* Edit modal */}
            {editingQuestion && (
              <Card className="p-4 bg-amber-50 border-amber-300 mb-4">
                <div className="font-semibold text-sm mb-3">
                  Editing Question {editingQuestion.questionNumber}
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs mb-1 block">Question Text</Label>
                    <textarea
                      value={editingQuestion.text}
                      onChange={(e) =>
                        setEditingQuestion({ ...editingQuestion, text: e.target.value })
                      }
                      className="w-full border border-slate-300 rounded-md p-2 text-sm"
                      rows={3}
                    />
                  </div>
                  {editingQuestion.options.map((opt, idx) => (
                    <div key={idx}>
                      <Label className="text-xs mb-1 block">Option {String.fromCharCode(65 + idx)}</Label>
                      <input
                        value={opt}
                        onChange={(e) => {
                          const newOptions = [...editingQuestion.options];
                          newOptions[idx] = e.target.value;
                          setEditingQuestion({ ...editingQuestion, options: newOptions });
                        }}
                        className="w-full border border-slate-300 rounded-md p-2 text-sm"
                      />
                    </div>
                  ))}
                  <div>
                    <Label className="text-xs mb-1 block">Correct Answer</Label>
                    <Select
                      value={editingQuestion.correctAnswer}
                      onValueChange={(v) =>
                        setEditingQuestion({ ...editingQuestion, correctAnswer: v })
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {editingQuestion.options.map((opt, idx) => (
                          <SelectItem key={idx} value={opt}>
                            {String.fromCharCode(65 + idx)}: {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveEdit} className="bg-emerald-600 hover:bg-emerald-700">
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingQuestion(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Questions list */}
            <div className="space-y-3">
              {questions.map((q) => {
                const state = questionStates[q.question_number];
                const isApproved = state === "approved";
                const isRejected = state === "rejected";

                return (
                  <Card
                    key={q.question_number}
                    className={`p-4 ${
                      isApproved
                        ? "bg-emerald-50 border-emerald-300"
                        : isRejected
                        ? "bg-slate-100 border-slate-300"
                        : "bg-white border-slate-200"
                    }`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                      <div className="font-semibold text-slate-900 flex flex-wrap items-center gap-2">
                        <span>Question {q.question_number}</span>
                        {isApproved && (
                          <span className="text-xs bg-emerald-600 text-white px-2 py-0.5 rounded">
                            ✓ Approved
                          </span>
                        )}
                        {isRejected && (
                          <span className="text-xs bg-slate-500 text-white px-2 py-0.5 rounded">
                            Rejected
                          </span>
                        )}
                        {testData?.add_marks && q.marks && (
                          <span className="text-xs bg-sky-100 text-sky-800 px-2 py-0.5 rounded font-semibold">
                            {q.marks} {q.marks === 1 ? 'mark' : 'marks'}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        {testData?.add_marks && (
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-slate-600">Marks:</Label>
                            <Select
                              value={String(q.marks || 1)}
                              onValueChange={(v) => {
                                const updatedQuestions = questions.map((qt) =>
                                  qt.question_number === q.question_number
                                    ? { ...qt, marks: parseInt(v, 10) }
                                    : qt
                                );
                                setQuestions(updatedQuestions);
                              }}
                            >
                              <SelectTrigger className="w-16 h-7 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {[1, 2, 3, 4, 5].map((m) => (
                                  <SelectItem key={m} value={String(m)}>
                                    {m}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div className="text-xs text-slate-500">
                          {q.sub_topic} · {q.difficulty}
                        </div>
                      </div>
                    </div>

                    {!isRejected && (
                      <>
                        <p className="text-sm text-slate-800 mb-3">{q.question_text}</p>
                        <div className="space-y-1.5 mb-3">
                          {q.options.map((opt, idx) => {
                            const isCorrect = opt === q.correct_answer;
                            return (
                              <div
                                key={idx}
                                className={`text-sm p-2 rounded-md ${
                                  isCorrect
                                    ? "bg-emerald-100 border border-emerald-300 font-semibold"
                                    : "bg-slate-50"
                                }`}
                              >
                                <span className="font-semibold mr-2">
                                  {String.fromCharCode(65 + idx)}.
                                </span>
                                {opt}
                                {isCorrect && (
                                  <span className="ml-2 text-xs text-emerald-700">
                                    (Correct answer - visible to teacher only)
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {isRejected && (
                      <p className="text-sm text-slate-500 italic mb-3">
                        Rejected — use Refresh to replace
                      </p>
                    )}

                    {/* Action buttons */}
                    <div className="flex gap-2 flex-wrap">
                      {!isApproved && !isRejected && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleApprove(q.question_number)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8"
                          >
                            <Check className="w-3.5 h-3.5 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(q)}
                            className="text-xs h-8"
                          >
                            <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReject(q.question_number)}
                            className="text-xs h-8"
                          >
                            <X className="w-3.5 h-3.5 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRefresh(q)}
                        className="text-xs h-8"
                      >
                        <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Bottom action bar with Approve All */}
            <div className="flex flex-wrap items-center justify-between gap-2 pt-4 border-t">
              <Button
                size="sm"
                onClick={handleApproveAll}
                variant="outline"
                className="text-xs"
              >
                <Check className="w-3.5 h-3.5 mr-1" /> Approve All
              </Button>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  onClick={handleFinalize}
                  disabled={loading}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Finalizing...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      Finalize Test
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Phase: Manual Builder */}
        {phase === "manual_builder" && (
          <div className="py-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm text-slate-500">
                  Add questions below. MCQ questions are auto-scored; TITA (free text) you review manually.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => setPhase("form")} className="text-xs">
                  Back
                </Button>
              <Button
                  size="sm"
                  onClick={handleManualPreview}
                  className="bg-violet-600 hover:bg-violet-700 text-xs"
                  data-testid="manual-preview-btn"
                >
                  <FileText className="w-3.5 h-3.5 mr-1" /> Preview Paper
                </Button>
              </div>
            </div>

            <ManualTestBuilder
              questions={manualQuestions}
              onChange={setManualQuestions}
              testMeta={{
                grade: localGrade,
                subject: formData.subject,
                chapter: formData.chapter,
                learnerName,
              }}
            />
          </div>
        )}

        {/* Phase: Manual Preview (read-only view before publish) */}
        {phase === "manual_preview" && (
          <div className="py-4 space-y-4">
            {/* Header */}
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-teacher text-base font-bold text-violet-900">Paper Preview</h3>
                  <p className="text-xs text-violet-600 mt-0.5">
                    {formData.subject} · {formData.chapter} · Grade {localGrade} · {manualQuestions.length} question{manualQuestions.length !== 1 ? "s" : ""}
                    {learnerName && <span> · For {learnerName}</span>}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPhase("manual_builder")}
                    className="text-xs border-violet-300 text-violet-700"
                    data-testid="manual-preview-back-btn"
                  >
                    Back to Edit
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleManualPublishClick}
                    disabled={manualPublishLoading || manualStudentsLoading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-xs"
                    data-testid="manual-publish-btn"
                  >
                    {manualPublishLoading ? (
                      <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Publishing...</>
                    ) : manualStudentsLoading ? (
                      <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Loading...</>
                    ) : learnerId ? (
                      "Publish Test"
                    ) : (
                      <><Users className="w-3.5 h-3.5 mr-1" /> Select Students &amp; Publish</>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Questions read-only list */}
            <div className="space-y-4">
              {manualQuestions.map((q, idx) => (
                <div key={idx} className="border border-slate-200 rounded-xl p-4 bg-white">
                  <div className="flex items-start gap-3 mb-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-slate-100 text-slate-700 text-xs font-bold flex items-center justify-center">
                      {idx + 1}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-slate-500 uppercase">
                          {q.answer_type}
                        </span>
                        {q.marks && (
                          <span className="text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded font-semibold">
                            {q.marks} {q.marks === 1 ? "mark" : "marks"}
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-slate-800">{q.question_text}</p>
                      {q.question_image && (
                        <img src={q.question_image} alt="Question" className="mt-2 max-h-40 rounded-lg border border-slate-200 object-contain" />
                      )}
                    </div>
                  </div>
                  {q.answer_type === "MCQ" && (
                    <div className="ml-10 space-y-1.5">
                      {(q.options || []).map((opt, oi) => (
                        <div
                          key={oi}
                          className={`text-sm px-3 py-1.5 rounded-lg ${
                            opt === q.correct_answer
                              ? "bg-emerald-50 border border-emerald-300 font-semibold text-emerald-800"
                              : "bg-slate-50 text-slate-700"
                          }`}
                        >
                          <span className="font-semibold mr-2">{String.fromCharCode(65 + oi)}.</span>
                          {opt}
                          {opt === q.correct_answer && (
                            <span className="ml-2 text-xs text-emerald-600">(Correct)</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {q.answer_type === "TITA" && (
                    <div className="ml-10">
                      <div className="text-xs text-slate-400 border-t border-dashed border-slate-200 pt-2 mt-2">
                        Free-text answer — teacher reviews manually
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Individual student: marks-only dialog */}
            {showPublishDialog && learnerId && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <Card className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl space-y-4">
                  <h3 className="font-teacher text-lg font-bold text-slate-900">Publish Test</h3>
                  <p className="text-sm text-slate-600">
                    Should marks be visible to {learnerName || "this student"} when they attempt this test?
                  </p>
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => handleManualPublish(true)}
                      className="bg-emerald-600 hover:bg-emerald-700 w-full"
                      data-testid="publish-with-marks-btn"
                    >
                      Publish with marks
                    </Button>
                    <Button
                      onClick={() => handleManualPublish(false)}
                      variant="outline"
                      className="w-full"
                      data-testid="publish-without-marks-btn"
                    >
                      Publish without marks
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setShowPublishDialog(false)}
                      className="w-full text-slate-500"
                    >
                      Cancel
                    </Button>
                  </div>
                </Card>
              </div>
            )}

            {/* Class test: student select + marks dialog */}
            {showManualStudentSelect && (
              <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="manual-student-select-modal">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto overflow-hidden" style={{ maxHeight: "90vh" }}>
                  <div className="flex items-center justify-between p-5 border-b">
                    <div>
                      <h3 className="text-base font-bold text-gray-900">Select Students &amp; Publish</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {manualSelectedIds.length} of {manualStudentList.length} selected
                      </p>
                    </div>
                    <button onClick={() => setShowManualStudentSelect(false)} className="p-1 rounded hover:bg-gray-100 text-gray-500">
                      <X size={18} />
                    </button>
                  </div>

                  <div className="overflow-y-auto" style={{ maxHeight: "calc(90vh - 220px)" }}>
                    {/* Select all */}
                    <div
                      className="flex items-center gap-3 px-5 py-3 border-b bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => {
                        const allSel = manualSelectedIds.length === manualStudentList.length;
                        setManualSelectedIds(allSel ? [] : manualStudentList.map(s => s.learner_id));
                      }}
                      data-testid="manual-select-all-students"
                    >
                      {manualSelectedIds.length === manualStudentList.length && manualStudentList.length > 0
                        ? <CheckSquare size={18} className="text-emerald-600 flex-shrink-0" />
                        : <Square size={18} className="text-gray-400 flex-shrink-0" />}
                      <span className="text-sm font-semibold text-gray-700">Entire Class</span>
                      <span className="ml-auto text-xs text-gray-400">{manualStudentList.length} students</span>
                    </div>

                    {manualStudentList.map(s => {
                      const checked = manualSelectedIds.includes(s.learner_id);
                      return (
                        <div
                          key={s.learner_id}
                          onClick={() => setManualSelectedIds(prev =>
                            prev.includes(s.learner_id)
                              ? prev.filter(id => id !== s.learner_id)
                              : [...prev, s.learner_id]
                          )}
                          className="flex items-center gap-3 px-5 py-3 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100"
                          data-testid={`manual-student-${s.learner_id}`}
                        >
                          {checked
                            ? <CheckSquare size={17} className="text-emerald-600 flex-shrink-0" />
                            : <Square size={17} className="text-gray-400 flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{s.name || s.learner_id}</p>
                            {s.name && <p className="text-xs text-gray-400 truncate">{s.learner_id}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Marks toggle + publish */}
                  <div className="p-5 border-t bg-gray-50 space-y-3">
                    <p className="text-xs font-semibold text-gray-600">Marks visibility:</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setManualMarksOption(true)}
                        className={`flex-1 py-2 text-xs font-semibold rounded-lg border-2 transition-colors ${manualMarksOption ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
                        data-testid="manual-marks-with"
                      >
                        With marks
                      </button>
                      <button
                        onClick={() => setManualMarksOption(false)}
                        className={`flex-1 py-2 text-xs font-semibold rounded-lg border-2 transition-colors ${!manualMarksOption ? "border-slate-500 bg-slate-50 text-slate-700" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
                        data-testid="manual-marks-without"
                      >
                        Without marks
                      </button>
                    </div>
                    <div className="flex gap-3 pt-1">
                      <Button variant="outline" onClick={() => setShowManualStudentSelect(false)} className="flex-1 text-sm">
                        Cancel
                      </Button>
                      <Button
                        onClick={() => handleManualPublish(manualMarksOption, manualSelectedIds)}
                        disabled={manualPublishLoading || manualSelectedIds.length === 0}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-sm"
                        data-testid="manual-confirm-publish-btn"
                      >
                        {manualPublishLoading
                          ? <><Loader2 size={14} className="mr-1 animate-spin" /> Publishing…</>
                          : <><Send className="w-3.5 h-3.5 mr-1" /> Publish to {manualSelectedIds.length}</>}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Phase 4: Preview/Download */}
        {phase === "preview" && (
          <div className="py-4">
            <TestPDF
              testData={{
                ...testData,
                grade: testData?.grade || grade,
                questions: testData?.mode === "manual" ? manualQuestions : questions,
              }}
              teacherId={teacherId}
              grade={testData?.grade || grade}
              onClose={handleClose}
              onBack={() => setPhase(mode === "manual" ? "manual_preview" : "review")}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
